import {
	Card,
	CardBody,
	CardHeader,
	CardHeading,
	FormikWithRedirectForm,
	Loading,
	MenuItemSingleSelect,
	SingleSelectField,
	SubmitButton,
	SupportedLanguageSingleSelectField,
	TextField,
	isSuccess,
	useHasPermission,
	useToasts,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useField } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ConfigurationsShowResource, TStartPageBehaviour } from "../../../shared/types/configurations";
import { PermissionName } from "../../../shared/types/permission";
import { configurationsService } from "../shared/api/configurationsService";
import { isValidEmail } from "../shared/utils/email";

const Actions = styled.div`
	display: flex;
	justify-content: end;
`;

const StyledCard = styled(Card)`
	margin: ${({ theme }) => theme.spacing(8)};
`;

export const ConfigurationsPage: FC = () => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const validate = useCallback(
		(values: ConfigurationsShowResource) => {
			const errors: { [K in keyof ConfigurationsShowResource]?: string } = {};

			if (!values.logo) {
				errors.logo = t("Required field");
			}

			if (!values.company) {
				errors.company = t("Required field");
			}

			if (!values.language) {
				errors.language = t("Required field");
			}

			if (!values.appIdentifier) {
				errors.appIdentifier = t("Required field");
			}

			if (values.startPageBehaviour === "menuItem" && !values.menuItem) {
				errors.menuItem = t("Required field");
			}

			if (values.contactEmail && !isValidEmail(values.contactEmail)) {
				errors.contactEmail = t("Invalid email address");
			}

			return errors;
		},
		[t],
	);

	const { data: initialValues, isLoading } = useQuery({
		queryKey: configurationsService.showKey,
		queryFn: () => configurationsService.show().then((r) => r.data.data),
	});

	const handleSubmit = async (values: ConfigurationsShowResource) => {
		const result = await configurationsService.upsert({
			logo: values.logo ?? "",
			company: values.company ?? "",
			appIdentifier: values.appIdentifier ?? "",
			contactEmail: values.contactEmail,
			contactName: values.contactName,
			languageIsoCode: values.language?.isoCode ?? null,
			startPageBehaviour: values.startPageBehaviour,
			menuItemId: values.menuItem?.id ?? null,
		});

		if (isSuccess(result)) {
			showToast({ title: t("Settings"), type: "success", body: t("The settings have been saved.") });
			void queryClient.invalidateQueries({ queryKey: configurationsService.showKey });
			return true;
		}

		return false;
	};

	const canUpdate = useHasPermission<PermissionName>(["configurations.update"]);

	return (
		<StyledCard>
			<CardHeader>
				<CardHeading>{t("Settings")}</CardHeading>
			</CardHeader>
			<CardBody>
				{isLoading || initialValues === undefined ? (
					<Loading />
				) : (
					<FormikWithRedirectForm onSubmit={handleSubmit} initialValues={initialValues} validate={validate}>
						<TextField name="logo" label={t("Logo")} required={true} />
						<TextField name="company" label={t("Company")} required={true} />
						<TextField name="appIdentifier" label={t("HTML-Title")} required={true} />
						<SupportedLanguageSingleSelectField label={t("Language")} name="language" required={true} />
						<TextField name="contactName" label={t("Contact name")} />
						<TextField name="contactEmail" label={t("Contact email")} />
						<StartPageBehaviourSwitcher />
						<Actions>
							<SubmitButton disabled={!canUpdate}>{t("Save")}</SubmitButton>
						</Actions>
					</FormikWithRedirectForm>
				)}
			</CardBody>
		</StyledCard>
	);
};

const startPageBehaviorOptions: TStartPageBehaviour[] = ["menuItem", "eventCalendar"];
const StartPageBehaviourSwitcher: FC = () => {
	const [{ value }] = useField<TStartPageBehaviour>("startPageBehaviour");
	const { t } = useTranslation();

	return (
		<>
			<SingleSelectField
				name="startPageBehaviour"
				label={t("Initial view for the user")}
				options={startPageBehaviorOptions}
				getLabel={(x) => {
					if (x === "menuItem") {
						return t("Navigate to menu item");
					}
					return t("Navigate to event calendar");
				}}
				description={t("When opening the website, the user is redirected to the element defined here.")}
				clearable
			/>
			{value === "menuItem" && <MenuItemSingleSelect name="menuItem" label={t("Menu Item")} required />}
		</>
	);
};
